import React, { useRef } from 'react'
import { Link, useParams } from 'react-router-dom'
import { UserLayout } from 'components/layout/UserLayout'
import { DetailPageBar } from 'components/layout/PageBar'
import { useSprintPacket } from 'hooks/api/useSprintPackets'
import { PacketEditorBody } from './editor/PacketEditorBody'
import { SectionKey, unreviewedSlideKeys } from './editor/sections'
import { usePacketFonts } from './usePacketFonts'

export function EditSprintPacket() {
  usePacketFonts()
  const { businessID, packetID } = useParams<{ businessID: string; packetID: string }>()
  const packetIDNum = Number(packetID)
  const { packet } = useSprintPacket(packetIDNum)

  // Carried-forward slides nobody has looked at yet. Deliberately shown here,
  // beside the Present button, and never inside Present itself — the client may
  // be in the room. It never blocks presenting.
  const unreviewed = packet ? unreviewedSlideKeys(packet) : []
  const unreviewedCount = unreviewed.length

  // The count is the way to the slides, not just a tally: each click lands on the
  // next flagged slide below the last one jumped to, wrapping after the last.
  // Picked in page order off the DOM rather than the array, since that is the
  // order the AE scrolls in. Keyed off the last slide rather than an index, so
  // marking one reviewed in between doesn't skip its neighbour.
  const lastJumped = useRef<SectionKey | null>(null)
  const jumpToNextUnreviewed = () => {
    const flagged = new Set<string>(unreviewed)
    const slides = Array.from(document.querySelectorAll<HTMLElement>('[data-section-key]')).filter(
      el => flagged.has(el.dataset.sectionKey ?? '')
    )
    if (slides.length === 0) return

    const last = lastJumped.current
      ? document.querySelector(`[data-section-key="${lastJumped.current}"]`)
      : null
    const next =
      (last &&
        slides.find(el => last.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING)) ||
      slides[0]

    lastJumped.current = next.dataset.sectionKey as SectionKey
    next.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <UserLayout>
      <DetailPageBar
        eyebrow="Sprint Packet"
        subject={packet?.sprint?.name ?? 'Sprint'}
        backTo={`/${businessID}/sprint-packets`}
        actions={
          <>
            {unreviewedCount > 0 && (
              <button
                type="button"
                onClick={jumpToNextUnreviewed}
                title="Jump to the next slide not reviewed"
                className="inline-flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700 transition-colors hover:bg-amber-100"
              >
                {unreviewedCount} slide{unreviewedCount === 1 ? '' : 's'} not reviewed
                <i className="fa fa-arrow-down" aria-hidden="true" />
              </button>
            )}
            <Link
              to={`/${businessID}/sprint-packets/${packetID}/review`}
              className="inline-flex items-center gap-2 rounded-lg border border-mm-primary-600 px-4 py-2 text-sm font-semibold text-mm-primary-600 transition-colors hover:bg-mm-primary-50"
            >
              <i className="fa fa-check" />
              Review
            </Link>
            <Link
              to={`/${businessID}/sprint-packets/${packetID}/present`}
              className="inline-flex items-center gap-2 rounded-lg bg-mm-primary-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-mm-primary-700"
            >
              <i className="fa fa-play" />
              Present
            </Link>
          </>
        }
      />
      <PacketEditorBody businessID={Number(businessID)} packetID={packetIDNum} />
    </UserLayout>
  )
}
